if (!customElements.get('cart-progress-bar')) {
  let giftTiers = [];
  let mutationRegistered = false;
  const pendingGiftIds = new Set();

  /**
   * Liquid Ajax Cart mutation: auto-add / auto-remove free_product gifts
   * based on cart subtotal. Runs inside the same request queue as the user's
   * cart action (important: true), so it does not start a second loading cycle.
   * @see https://liquid-ajax-cart.js.org/v2/cart-mutations/
   * @returns {{requests: Array<{type: string, body: object}>}|undefined}
   */
  function tieredGiftMutation() {
    const cart = window.liquidAjaxCart?.cart;
    if (!cart?.items || !giftTiers.length) return;

    const subtotal = cart.total_price;
    const updates = {};
    const itemsToAdd = [];

    for (const tier of giftTiers) {
      const unlocked = tier.threshold > 0 && subtotal >= tier.threshold;
      const giftItem = cart.items.find(
        (item) =>
          (item.variant_id === tier.giftId || item.id === tier.giftId) &&
          item.properties?._tiered_free_gift === 'true'
      );

      if (unlocked && !giftItem) {
        if (pendingGiftIds.has(tier.giftId)) continue;
        itemsToAdd.push({
          id: tier.giftId,
          quantity: 1,
          properties: {
            _tiered_free_gift: 'true',
            _source: 'progress_bar',
          },
        });
        pendingGiftIds.add(tier.giftId);
      } else if (!unlocked && giftItem) {
        updates[giftItem.key] = 0;
        pendingGiftIds.delete(tier.giftId);
      } else if (giftItem) {
        pendingGiftIds.delete(tier.giftId);
        if (giftItem.quantity !== 1) {
          updates[giftItem.key] = 1;
        }
      }
    }

    const requests = [];
    if (Object.keys(updates).length) {
      requests.push({ type: 'update', body: { updates } });
    }
    if (itemsToAdd.length) {
      requests.push({ type: 'add', body: { items: itemsToAdd } });
    }

    return requests.length ? { requests } : undefined;
  }

  function registerMutation() {
    if (mutationRegistered || !window.liquidAjaxCart?.conf) return;
    window.liquidAjaxCart.conf('mutations', [tieredGiftMutation]);
    mutationRegistered = true;
  }

  /**
   * Safety-net direct check (same logic as the mutation). Guarantees the first
   * sync runs even if library-internal mutation timing does not fire yet.
   * pendingGiftIds prevents double-add when mutation + this check both run.
   */
  function runGiftCheck() {
    if (!window.liquidAjaxCart?.add || !window.liquidAjaxCart?.update) return;

    const result = tieredGiftMutation();
    if (!result) return;

    for (const req of result.requests) {
      const options = { info: { initiator: 'mutation' } };

      if (req.type === 'update') {
        window.liquidAjaxCart.update(req.body, options);
      }

      if (req.type === 'add') {
        const addedIds = (req.body.items || []).map((item) => item.id);
        window.liquidAjaxCart.add(req.body, {
          ...options,
          lastCallback: () => {
            for (const id of addedIds) pendingGiftIds.delete(id);
          },
        });
      }
    }
  }

  class CartProgressBar extends HTMLElement {
    connectedCallback() {
      giftTiers = this.parseTiers();
      if (!giftTiers.length) return;

      if (window.liquidAjaxCart?.conf) {
        registerMutation();
      } else {
        document.addEventListener('liquid-ajax-cart:init', registerMutation, { once: true });
      }

      if (window.liquidAjaxCart?.cart) {
        runGiftCheck();
      } else {
        document.addEventListener('liquid-ajax-cart:init', runGiftCheck, { once: true });
      }
    }

    parseTiers() {
      try {
        const parsed = JSON.parse(this.dataset.giftTiers || '[]');
        return Array.isArray(parsed)
          ? parsed.filter((tier) => tier && tier.giftId && tier.type === 'free_product')
          : [];
      } catch {
        return [];
      }
    }
  }

  customElements.define('cart-progress-bar', CartProgressBar);
}
